// In-app image viewer. Replaces the old `<a target="_blank">` pattern that
// kept losing the teacher's session context whenever the browser reused or
// killed the popup tab.
//
// Click an <img> wrapped by useLightbox() → a portal-rendered modal appears
// over the current page. The image scales to fit; the toolbar exposes Close
// (Esc), Previous / Next (←/→) when a gallery is provided, and Download
// (anchor with `download` attribute) so the teacher can keep a copy without
// the browser deciding to navigate away.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, ChevronLeft, ChevronRight } from "lucide-react";

export interface LightboxItem {
  url: string;
  alt?: string;
  filename?: string;
}

interface LightboxState {
  items: LightboxItem[];
  index: number;
}

interface LightboxContextValue {
  open: (items: LightboxItem[] | string[], index?: number, alt?: string) => void;
}

const LightboxContext = React.createContext<LightboxContextValue | null>(null);

export function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LightboxState | null>(null);

  const open = useCallback((items: LightboxItem[] | string[], index = 0, alt?: string) => {
    const normalised: LightboxItem[] = items.map((it, i) =>
      typeof it === "string"
        ? { url: it, alt: alt ?? `Image ${i + 1}` }
        : it,
    );
    setState({ items: normalised, index: Math.max(0, Math.min(index, normalised.length - 1)) });
  }, []);

  const close = useCallback(() => setState(null), []);

  const value = useMemo<LightboxContextValue>(() => ({ open }), [open]);

  useEffect(() => {
    if (!state) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { close(); return; }
      if (e.key === "ArrowRight") setState(s => s ? { ...s, index: Math.min(s.items.length - 1, s.index + 1) } : s);
      if (e.key === "ArrowLeft")  setState(s => s ? { ...s, index: Math.max(0, s.index - 1) } : s);
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [state, close]);

  return (
    <LightboxContext.Provider value={value}>
      {children}
      {state && createPortal(
        <Modal state={state}
               onClose={close}
               onPrev={() => setState(s => s ? { ...s, index: Math.max(0, s.index - 1) } : s)}
               onNext={() => setState(s => s ? { ...s, index: Math.min(s.items.length - 1, s.index + 1) } : s)}
        />,
        document.body,
      )}
    </LightboxContext.Provider>
  );
}

export function useLightbox(): LightboxContextValue {
  const ctx = React.useContext(LightboxContext);
  if (!ctx) return { open: () => {} };
  return ctx;
}

function Modal({
  state, onClose, onPrev, onNext,
}: {
  state: LightboxState; onClose: () => void; onPrev: () => void; onNext: () => void;
}) {
  const item = state.items[state.index];
  const downloadName = item.filename ?? deriveFilename(item.url);
  const multi = state.items.length > 1;

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-[100] flex flex-col bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="flex items-center justify-between gap-2 p-3 sm:p-4" onClick={(e) => e.stopPropagation()}>
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400">
          {multi ? `${state.index + 1} / ${state.items.length}` : "Preview"}
        </div>
        <div className="flex items-center gap-2">
          <a href={item.url} download={downloadName}
             className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-medium border border-white/10">
            <Download size={14}/> Download
          </a>
          <button onClick={onClose} aria-label="Close"
            className="p-2 rounded-lg bg-white/10 hover:bg-white/15 text-white border border-white/10">
            <X size={14}/>
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-2 sm:px-6 pb-4 select-none">
        {multi && (
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
            disabled={state.index === 0}
            aria-label="Previous image"
            className="absolute left-2 sm:left-4 p-2 rounded-full bg-white/10 hover:bg-white/15 disabled:opacity-30 text-white border border-white/10">
            <ChevronLeft size={18}/>
          </button>
        )}
        <img
          src={item.url}
          alt={item.alt || "image"}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain rounded shadow-2xl"
          style={{ maxHeight: "calc(100vh - 6rem)" }}
        />
        {multi && (
          <button onClick={(e) => { e.stopPropagation(); onNext(); }}
            disabled={state.index === state.items.length - 1}
            aria-label="Next image"
            className="absolute right-2 sm:right-4 p-2 rounded-full bg-white/10 hover:bg-white/15 disabled:opacity-30 text-white border border-white/10">
            <ChevronRight size={18}/>
          </button>
        )}
      </div>
    </div>
  );
}

function deriveFilename(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const tail = u.pathname.split("/").filter(Boolean).pop();
    if (tail && /\.[a-z0-9]+$/i.test(tail)) return tail;
  } catch { /* fall through */ }
  return "image.jpg";
}
